import { ref } from 'vue';
import loadScript from '@app/utils/loadScript.js';
import debounce from '@app/utils/debounce.js';

const MAPBOX_JS_URL = 'https://api.mapbox.com/mapbox-gl-js/v3.12.0/mapbox-gl.js';

export function formatPrice(price) {
  if (price >= 1000000) return `$${(price / 1000000).toFixed(1)}M`;
  if (price >= 1000) return `$${(price / 1000).toFixed(0)}K`;
  return `$${price}`;
}

const MARKER_COLORS = { rent: '#0043FF', sale: '#FF1200' };
const markerImageCache = new Map();

function createPillMarker(text, color, pixelRatio = 2) {
  const key = `${text}-${color}`;
  if (markerImageCache.has(key)) return markerImageCache.get(key);

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const fontSize = 12 * pixelRatio;
  const paddingX = 10 * pixelRatio;
  const paddingY = 6 * pixelRatio;
  const pointerH = 6 * pixelRatio;
  const radius = 8 * pixelRatio;

  ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  const metrics = ctx.measureText(text);
  const textW = metrics.width;

  const w = textW + paddingX * 2;
  const bodyH = fontSize + paddingY * 2;
  const h = bodyH + pointerH;

  canvas.width = w;
  canvas.height = h;

  // Re-set font after resize
  ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;

  // Shadow
  ctx.shadowColor = 'rgba(0,0,0,0.25)';
  ctx.shadowBlur = 4 * pixelRatio;
  ctx.shadowOffsetY = 2 * pixelRatio;

  // Draw pill body with pointer
  ctx.beginPath();
  ctx.moveTo(radius, 0);
  ctx.lineTo(w - radius, 0);
  ctx.arcTo(w, 0, w, radius, radius);
  ctx.lineTo(w, bodyH - radius);
  ctx.arcTo(w, bodyH, w - radius, bodyH, radius);
  // Pointer triangle
  ctx.lineTo(w / 2 + pointerH, bodyH);
  ctx.lineTo(w / 2, h);
  ctx.lineTo(w / 2 - pointerH, bodyH);
  ctx.lineTo(radius, bodyH);
  ctx.arcTo(0, bodyH, 0, bodyH - radius, radius);
  ctx.lineTo(0, radius);
  ctx.arcTo(0, 0, radius, 0, radius);
  ctx.closePath();

  ctx.fillStyle = color;
  ctx.fill();

  // Reset shadow for text
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  // Draw text
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, w / 2, bodyH / 2);

  const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const result = { data, width: canvas.width, height: canvas.height, cssWidth: w / pixelRatio, cssHeight: h / pixelRatio };
  markerImageCache.set(key, result);
  return result;
}

function toGeoJSON(points) {
  return {
    type: 'FeatureCollection',
    features: points.map((p) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
      properties: {
        id: p.id,
        price: p.price,
        priceLabel: formatPrice(p.price),
        type: p.type, // 'sale' | 'rent'
        markerImage: `pill-${formatPrice(p.price)}-${p.type}`,
      },
    })),
  };
}

function promisifyCluster(fn, ...args) {
  return new Promise((resolve, reject) => {
    fn(...args, (err, result) => (err ? reject(err) : resolve(result)));
  });
}

export function useMapbox(accessToken, { onBoundsChange, onMarkerClick, onClusterClick }) {
  const mapReady = ref(false);
  let map = null;
  let popup = null;

  const debouncedBoundsChange = debounce(() => {
    if (!map) return;
    const b = map.getBounds();
    onBoundsChange({
      north: b.getNorth(),
      south: b.getSouth(),
      east: b.getEast(),
      west: b.getWest(),
    });
  }, 300);

  async function initMap(container) {
    await loadScript(MAPBOX_JS_URL);

    mapboxgl.accessToken = accessToken;

    map = new mapboxgl.Map({
      container,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [-89.2, 13.7], // El Salvador
      zoom: 8,
    });

    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    map.on('load', () => {
      // GeoJSON source with clustering
      map.addSource('properties', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
        promoteId: 'id',
      });

      // Cluster circles
      map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'properties',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': '#003DA5',
          'circle-radius': [
            'step', ['get', 'point_count'],
            20,   // default
            10, 25,
            50, 30,
            100, 35,
          ],
          'circle-opacity': 0.85,
        },
      });

      // Cluster count label
      map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'properties',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-size': 13,
          'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
        },
        paint: {
          'text-color': '#ffffff',
        },
      });

      // Unclustered price pill markers
      map.addLayer({
        id: 'unclustered-point',
        type: 'symbol',
        source: 'properties',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': ['get', 'markerImage'],
          'icon-size': 1,
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true,
        },
      });

      // Click on cluster -> show property list or zoom in
      map.on('click', 'clusters', async (e) => {
        const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        const cluster = features[0];
        const clusterId = cluster.properties.cluster_id;
        const pointCount = cluster.properties.point_count;
        const coords = cluster.geometry.coordinates.slice();
        const source = map.getSource('properties');

        if (onClusterClick && pointCount <= 20) {
          try {
            const leaves = await promisifyCluster(source.getClusterLeaves.bind(source), clusterId, 20, 0);
            onClusterClick(leaves.map(f => f.properties), coords);
            return;
          } catch { /* fallback to zoom below */ }
        }

        try {
          const zoom = await promisifyCluster(source.getClusterExpansionZoom.bind(source), clusterId);
          map.easeTo({ center: coords, zoom });
        } catch { /* ignore */ }
      });

      // Click on unclustered point -> callback to parent
      map.on('click', 'unclustered-point', (e) => {
        const feature = e.features[0];
        const coords = feature.geometry.coordinates.slice();
        if (onMarkerClick) onMarkerClick(feature.properties.id, coords);
      });

      // Cursor changes
      map.on('mouseenter', 'clusters', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'clusters', () => { map.getCanvas().style.cursor = ''; });
      map.on('mouseenter', 'unclustered-point', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'unclustered-point', () => { map.getCanvas().style.cursor = ''; });

      // Bounds change
      map.on('moveend', debouncedBoundsChange);

      mapReady.value = true;

      // Fire initial bounds
      debouncedBoundsChange();
    });
  }

  function updateMapPoints(points) {
    if (!map || !map.getSource('properties')) return;

    // Register pill images for each unique price+type combo
    const pixelRatio = window.devicePixelRatio || 2;
    const seen = new Set();
    for (const p of points) {
      const label = formatPrice(p.price);
      const type = p.type || 'sale';
      const imageName = `pill-${label}-${type}`;
      if (seen.has(imageName) || map.hasImage(imageName)) {
        seen.add(imageName);
        continue;
      }
      seen.add(imageName);
      const color = MARKER_COLORS[type] || MARKER_COLORS.sale;
      const pill = createPillMarker(label, color, pixelRatio);
      map.addImage(imageName, pill.data, { pixelRatio });
    }

    map.getSource('properties').setData(toGeoJSON(points));
  }

  function highlightMarker(propertyId) {
    if (!map) return;
    // Reset all feature states
    map.removeFeatureState({ source: 'properties' });
    // Set highlighted state on matching feature
    if (propertyId != null) {
      map.setFeatureState(
        { source: 'properties', id: propertyId },
        { highlighted: true },
      );
    }
  }

  function showPopup(lngLat, html, options = {}) {
    if (!map) return;
    if (popup) popup.remove();
    popup = new mapboxgl.Popup({
      offset: options.offset ?? 20,
      maxWidth: options.maxWidth ?? '320px',
      closeButton: true,
      className: options.className ?? 'property-popup',
    })
      .setLngLat(lngLat)
      .setHTML(html)
      .addTo(map);
  }

  function closePopup() {
    if (popup) popup.remove();
    popup = null;
  }

  function flyTo(lng, lat, zoom = 15) {
    if (map) map.flyTo({ center: [lng, lat], zoom });
  }

  function resize() {
    if (map) map.resize();
  }

  function destroy() {
    if (popup) popup.remove();
    if (map) map.remove();
    map = null;
    popup = null;
  }

  return {
    mapReady,
    initMap,
    updateMapPoints,
    highlightMarker,
    showPopup,
    closePopup,
    flyTo,
    resize,
    destroy,
  };
}
